import { useAppDispatch, useAppSelector } from 'store/hooks';
import React, { useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Box, Button, Divider, Paper, TextField, styled, Typography } from '@mui/material';
import getValue from 'get-value';
import { MainLayout } from 'layouts/MainLayout';
import { contentApi } from 'components/content/contentApi';
import { LayoutConfigStepper } from 'components/content/layout-config/LayoutConfigStepper';
import { LayoutPreview } from 'components/content/layout-config/LayoutPreview';
import {
    MESSAGE_MOBILE_CONTENT_LAYOUT_SET_ACTIVE_ERROR,
    MESSAGE_MOBILE_CONTENT_LAYOUT_SET_ACTIVE_SUCCESS,
    MESSAGE_MOBILE_CONTENT_LAYOUT_SET_DESCRIPTION_ERROR
} from 'config/messages';
import { MOBILE_CONTENT } from 'config/routes';
import { clearCurrentModules, getLayoutFromId } from 'store/content/contentSlice';
import { useSnackbar, OptionsObject } from 'notistack';
import { Column, Row } from 'ui/Flex';

const PREFIX = 'mobile-confirm';

const classes = {
    row: `${PREFIX}-row`,
    config: `${PREFIX}-config`,
    preview: `${PREFIX}-preview`,
    activateContent: `${PREFIX}-activateContent`,
    activateBody: `${PREFIX}-activateBody`,
    activateButton: `${PREFIX}-activateButton`,
    input: `${PREFIX}-input`
};

const StyledRow = styled(Row)(({ theme }) => ({
    [`& .${classes.row}`]: {
        width: '100%'
    },
    [`& .${classes.config}`]: {
        flexBasis: '35%'
    },
    [`& .${classes.preview}`]: {
        paddingLeft: theme.spacing(4)
    },
    [`& .${classes.activateContent}`]: {
        padding: theme.spacing(2),
        width: '500px'
    },
    [`& .${classes.activateBody}`]: {
        margin: `${theme.spacing(3)} 0`
    },
    [`& .${classes.activateButton}`]: {
        marginLeft: '10px'
    },
    [`& .${classes.input}`]: {
        marginTop: theme.spacing(2)
    }
}));

const ConfirmationPage = () => {
    const { enqueueSnackbar } = useSnackbar();
    const content = useAppSelector(state => state.content);
    const dispatch = useAppDispatch();
    const [isLoading, setLoading] = useState(false);
    const [description, setDescription] = useState(
        getValue(content, 'currentLayout.anchor.metadata.description')
    );
    const navigate = useNavigate();
    const location = useLocation();
    const { layoutId } = useParams<{ layoutId: string }>();

    React.useEffect(() => {
        async function load() {
            if (layoutId) {
                dispatch(clearCurrentModules());
                await dispatch(getLayoutFromId(layoutId));
            }
        }
        load();
    }, [dispatch, location.search, layoutId]);

    const showNotification = React.useCallback(
        (message: string, options: OptionsObject) => {
            enqueueSnackbar(message, options);
        },
        [enqueueSnackbar]
    );

    const handleSaveDraft = React.useCallback(async () => {
        setLoading(true);
        const updateResponse = await contentApi.layouts.update(content.currentLayout.id, {
            body: { anchor: { ...content.currentLayout.anchor, metadata: { description } } }
        });
        if (!updateResponse.ok) {
            return showNotification(MESSAGE_MOBILE_CONTENT_LAYOUT_SET_DESCRIPTION_ERROR, {
                variant: 'error'
            });
        }
        setLoading(false);
        navigate(MOBILE_CONTENT);
    }, [navigate, showNotification, content?.currentLayout?.id, content?.currentLayout?.anchor, description]);

    const handleActivate = React.useCallback(async () => {
        setLoading(true);
        const updateResponse = await contentApi.layouts.update(content.currentLayout.id, {
            body: { anchor: { ...content.currentLayout.anchor, metadata: { description } } }
        });
        if (!updateResponse.ok) {
            return showNotification(MESSAGE_MOBILE_CONTENT_LAYOUT_SET_DESCRIPTION_ERROR, {
                variant: 'error'
            });
        }
        const activateResponse = await contentApi.layouts.setActiveLayout({
            layoutId: content.currentLayout.id
        });
        if (activateResponse.ok) {
            showNotification(MESSAGE_MOBILE_CONTENT_LAYOUT_SET_ACTIVE_SUCCESS, { variant: 'success' });
        } else {
            showNotification(MESSAGE_MOBILE_CONTENT_LAYOUT_SET_ACTIVE_ERROR, { variant: 'error' });
        }

        setLoading(false);
        navigate(MOBILE_CONTENT);
    }, [navigate, showNotification, description, content?.currentLayout?.id, content?.currentLayout?.anchor]);

    const handleDescriptionChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value),
        []
    );

    return (
        <MainLayout
            columnProps={{ align: 'center' }}
            breadcrumbs={[{ label: 'Mobile content', url: MOBILE_CONTENT }]}
            pageName="Confirmation"
            pageDescription="Configure and manage what appears in your application"
        >
            <LayoutConfigStepper />
            <StyledRow valign="center" align="center">
                <Column align="center" valign="center">
                    <Paper>
                        <Box
                            flexDirection="column"
                            display="flex"
                            alignItems="flex-start"
                            justifyContent="space-between"
                            className={classes.activateContent}
                        >
                            <Box>
                                <Typography variant="h6">Are you ready to activate your app?</Typography>
                            </Box>
                            <Box className={classes.input} width="100%">
                                <TextField
                                    fullWidth
                                    label="Layout description"
                                    value={description}
                                    onChange={handleDescriptionChange}
                                />
                                <Typography variant="caption" color="textSecondary">
                                    Provide a description to help you find your layout later
                                </Typography>
                            </Box>
                            <Box className={classes.activateBody}>
                                <Typography variant="caption" align="center">
                                    If this is the first time you have configured your app, it will take us a
                                    few days to get it to you. If you have previously configured your app,
                                    then these changes will be available immediately.
                                </Typography>
                            </Box>
                            <Box width="100%">
                                <Divider />
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    alignItems="center"
                                    justifyContent="flex-end"
                                    style={{ marginTop: '8px' }}
                                >
                                    <Box>
                                        <Button variant="outlined" onClick={handleSaveDraft}>
                                            Save Draft
                                        </Button>
                                    </Box>
                                    <Box>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleActivate}
                                            disabled={isLoading}
                                            className={classes.activateButton}
                                        >
                                            Activate
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </Column>
                <Column>
                    <LayoutPreview className={classes.preview} />
                </Column>
            </StyledRow>
        </MainLayout>
    );
};

export default ConfirmationPage;
